import React, { useState, useEffect } from "react";
import { postCompany, postEmployeeOrder } from "../../Utils/api";
import {
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Paper,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useNavigate } from "react-router";
import PopupGeneric from "../../Components/PopupGeneric/pop-up-generic";
import {
  Email as EmailIcon,
  Home as HomeIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Work as WorkIcon,
} from "@mui/icons-material";
import './add-company.css';
import { useAuth } from "../../Auth/AuthContext";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { validateCityState, validateEmail, validateName, validatePhone, validateZip } from "../../Utils/validations";

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming"
];

const AddCompany = () => {
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isPopupVisible, setPopupVisible] = useState(false);
  const [popupTitle, setPopupTitle] = useState("");
  const [popupMessage, setPopupMessage] = useState("");
  const navigate = useNavigate();
  const { companyId } = useAuth();

  const [companyData, setCompanyData] = useState({
    companyName: "",
    companyEmail: "",
    ownerEmail: "",
    ownerFirstName: "",
    ownerLastName: "",
    ownerDesignation: "Owner",
    companyAddress: "",
    companyCity: "",
    companyState: "",
    companyZipCode: "",
    companyContactNumber: "",
  });

  //handle submit function
  const handleSubmit = async () => {
    try {
      const companyPayload = {
        id: null,
        name: companyData.companyName,
        email: companyData.companyEmail,
        ownerEmail: companyData.ownerEmail,
        address: companyData.companyAddress,
        city: companyData.companyCity,
        state: companyData.companyState,
        zipCode: companyData.companyZipCode,
        contactNumber: '+1' + companyData.companyContactNumber,
        currentRecordIndicator: "Y"
      };

      // Create company first
      const createdCompany = await postCompany(companyPayload);

      // Create owner employee record
      const employeePayload = {
        username: companyData.ownerEmail,
        email: companyData.ownerEmail,
        firstName: companyData.ownerFirstName,
        lastName: companyData.ownerLastName,
        contactNumber: '+1' + companyData.companyContactNumber,
        empDesignation: companyData.ownerDesignation,
        empAddress: companyData.companyAddress,
        empCity: companyData.companyCity,
        empState: companyData.companyState,
        empZipCode: companyData.companyZipCode,
        companyId: createdCompany.id,
        currRecInd: "Y"
      };

      await postEmployeeOrder(employeePayload);

      setPopupTitle("Success");
      setPopupMessage(`Company and owner submitted successfully! A password has been sent to ${companyData.ownerEmail} to login to Pool Route Monitor app.`);
      setPopupVisible(true);
      resetForm();

      setTimeout(() => {
        navigate('/admin', { replace: true });
      }, 2000);

    } catch (error) {
      setPopupTitle("Alert");
      setPopupMessage("Failed to submit. Please try again.");
      setPopupVisible(true);
    }
  };

  // Updates form data
  const handleComChange = (e) => {
    setCompanyData({ ...companyData, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setCompanyData({
      companyName: "",
      companyEmail: "",
      ownerEmail: "",
      ownerFirstName: "",
      ownerLastName: "",
      ownerDesignation: "Owner",
      companyAddress: "",
      companyCity: "",
      companyState: "",
      companyZipCode: "",
      companyContactNumber: "",
    });
  };

  useEffect(() => {
    const isValid =
      validateName(companyData.companyName).isValid &&
      validateEmail(companyData.companyEmail).isValid &&
      validateEmail(companyData.ownerEmail).isValid &&
      validateName(companyData.ownerFirstName).isValid &&
      validateName(companyData.ownerLastName).isValid &&
      companyData.ownerDesignation.trim() !== "" &&
      companyData.companyAddress.trim() !== "" &&
      validateCityState(companyData.companyCity).isValid &&
      companyData.companyState.trim() !== "" &&
      validateZip(companyData.companyZipCode).isValid &&
      validatePhone(companyData.companyContactNumber).isValid;

    setIsSubmitDisabled(!isValid);
  }, [companyData]);

  return (
    <Box className="form-container">
      <Paper elevation={3} sx={{ padding: "16px", textAlign: "center" }}>
        <Typography variant="h5" sx={{ fontWeight: "bold", marginBottom: "16px" }}>
          Add Company
        </Typography>

        <Grid container spacing={4}>
          {/* Company Name */}
          <Grid item xs={12}>
            <TextField
              label="Company Name"
              name="companyName"
              required
              fullWidth
              value={companyData.companyName}
              onChange={(e) => {
                const value = e.target.value;
                if(validateName(value).validChars) handleComChange(e);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
              helperText="Only letters and spaces allowed."
            />
          </Grid>

          {/* Company Email ID */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Company Email ID"
              name="companyEmail"
              required
              fullWidth
              value={companyData.companyEmail}
              onChange={(e) => {
                const value = e.target.value;
                if(validateEmail(value).validChars) handleComChange(e);
              }}
              onBlur={(e) => {
                const value = e.target.value.trim();
                if (value && !validateEmail(value).isValid) {
                    console.error("Invalid email format");
                }
              }}
              InputProps={{
                  startAdornment: (
                      <InputAdornment position="start">
                          <EmailIcon />
                      </InputAdornment>
                  ),
              }}
              error={companyData.companyEmail && !validateEmail(companyData.companyEmail).isValid}
              helperText={
                companyData.companyEmail && !validateEmail(companyData.companyEmail).isValid
                      ? "Enter valid email ID"
                      : ""
              }
            />
          </Grid>

          {/* Owner Email ID */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Owner Email ID"
              name="ownerEmail"
              required
              fullWidth
              value={companyData.ownerEmail}
              onChange={(e) => {
                const value = e.target.value;
                if(validateEmail(value).validChars) handleComChange(e);
              }}
              onBlur={(e) => {
                const value = e.target.value.trim();
                if (value && !validateEmail(value).isValid) {
                    console.error("Invalid email format");
                }
              }}
              InputProps={{
                  startAdornment: (
                      <InputAdornment position="start">
                          <EmailIcon />
                      </InputAdornment>
                  ),
              }}
              error={companyData.ownerEmail && !validateEmail(companyData.ownerEmail).isValid}
              helperText={
                companyData.ownerEmail && !validateEmail(companyData.ownerEmail).isValid
                      ? "Enter valid email ID"
                      : ""
              }
            />
          </Grid>

          {/* Owner First Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Owner First Name"
              name="ownerFirstName"
              required
              fullWidth
              value={companyData.ownerFirstName}
              onChange={(e) => {
                const value = e.target.value;
                if(validateName(value).validChars) handleComChange(e);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
              helperText="Only letters and spaces allowed."
            />
          </Grid>

          {/* Owner Last Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Owner Last Name"
              name="ownerLastName"
              required
              fullWidth
              value={companyData.ownerLastName}
              onChange={(e) => {
                const value = e.target.value;
                if(validateName(value).validChars) handleComChange(e);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
              helperText="Only letters and spaces allowed."
            />
          </Grid>

          {/* Designation */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel id="designation-label">Designation</InputLabel>
              <Select
                labelId="designation-label"
                label="Designation"
                name="ownerDesignation"
                value={companyData.ownerDesignation}
                onChange={handleComChange}
                sx={{ textAlign: "left" }}
                startAdornment={
                  <InputAdornment position="start">
                    <WorkIcon />
                  </InputAdornment>
                }
              >
                <MenuItem value="Owner">Owner</MenuItem>
                <MenuItem value="Co-Owner">Co-Owner</MenuItem>
                <MenuItem value="Manager">Manager</MenuItem>
                <MenuItem value="Partner">Partner</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Company Address */}
          <Grid item xs={12}>
            <TextField
              label="Company Address"
              name="companyAddress"
              required
              fullWidth
              value={companyData.companyAddress}
              onChange={handleComChange}
              InputProps={{ startAdornment: (<InputAdornment position="start"><HomeIcon /></InputAdornment>) }}
            />
          </Grid>

          {/* Company city */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="City"
              name="companyCity"
              required
              fullWidth
              value={companyData.companyCity}
              onChange={(e) => {
                const value = e.target.value;
                if(validateCityState(value).validChars) handleComChange(e);
              }}
              InputProps={{ startAdornment: (<InputAdornment position="start"><LocationOnIcon /></InputAdornment>) }}
              helperText="Only letters and spaces allowed."
            />
          </Grid>

          {/* Company State - Dropdown */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel id="state-label">State</InputLabel>
              <Select
                labelId="state-label"
                label="State"
                name="companyState"
                value={companyData.companyState}
                onChange={handleComChange}
                sx={{ textAlign: "left" }}
                startAdornment={
                  <InputAdornment position="start">
                    <LocationOnIcon />
                  </InputAdornment>
                }
              >
                {US_STATES.map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Company Zip Code */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Zip Code"
              name="companyZipCode"
              fullWidth
              value={companyData.companyZipCode}
              required
              onChange={(e) => {
                const value = e.target.value;
                if(validateZip(value).validChars) handleComChange(e);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon />
                  </InputAdornment>
                ),
              }}
              helperText="5-digit number only."
            />
          </Grid>

          {/* Contact Number */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Contact Number"
              name="companyContactNumber"
              fullWidth
              value={companyData.companyContactNumber}
              required
              onChange={(e) => {
                const value = e.target.value;
                // Remove +1 prefix if present to get just the digits
                const digitsOnly = value.replace('+1', '').trim();
                if(validatePhone(digitsOnly).validChars) {
                  handleComChange({ target: { name: 'companyContactNumber', value: digitsOnly } });
                }
              }}
              error={companyData.companyContactNumber.length > 0 && companyData.companyContactNumber.length !== 10}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon sx={{ marginRight: '4px' }} />
                    <Typography sx={{ color: '#666', fontWeight: 500 }}>+1</Typography>
                  </InputAdornment>
                ),
              }}
              helperText="10-digit number only."
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 3, justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            sx={{ minWidth: '150px' }}
          >
            Submit
          </Button>
        </Box>
      </Paper>

      <PopupGeneric
        isVisible={isPopupVisible}
        onClose={() => setPopupVisible(false)}
        title={popupTitle}
        content={<p>{popupMessage}</p>}
        actions={
          <button
            onClick={() => setPopupVisible(false)}
            style={{
              marginTop: '10px',
              padding: '5px 10px',
              borderRadius: '5px',
              backgroundColor: '#003366',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            OK
          </button>
        }
      />
    </Box>
  );
};

export default AddCompany;
